import { useMemo, useState } from 'react';
import { Card, CardContent } from '../../ui';
import { SeriesChart } from '../../composite';
import { EMOTION_META, CHART_ORDER, STANCE_META } from '../../../constants/sentiment';
import { formatUTCDate } from '../../../utils/dates';

/**
 * The share of replies AGAINST the account, day by day - "is the room turning
 * on them" as one line. Drawn only when the points carry stance; an unlabelled
 * history says nothing rather than drawing a flat zero that would read as
 * "nobody is against them".
 */
/** A day needs this many stance-labelled replies before its share means anything. */
const MIN_DAY_BASE = 10;

function AgainstLine({ points }) {
	const series = points
		.map((p) => {
			const s = p.stance || {};
			const n = (s.for || 0) + (s.against || 0) + (s.none || 0);
			return n ? { x: p.bucketStart, share: (s.against || 0) / n, against: s.against || 0, n, solid: n >= MIN_DAY_BASE } : null;
		})
		.filter(Boolean);
	if (series.length === 0) return null;

	// The headline is the last day with a real base. "now 50%" from one reply
	// against out of two on a quiet day is noise wearing a headline's clothes,
	// and it sat beside a 292-reply day drawn at the same weight. Thin days are
	// still shown - faint, off the line - so a gap in attention is visible
	// without being read as a swing in opinion.
	const solid = series.filter((p) => p.solid);
	const headline = solid.length ? solid[solid.length - 1] : null;

	const W = 800, H = 64, padL = 30, padR = 8, padT = 8, padB = 14;
	const n = series.length;
	const xAt = (i) => (n === 1 ? W / 2 : padL + (i * (W - padL - padR)) / (n - 1));
	const yAt = (v) => padT + (H - padT - padB) * (1 - v);
	const solidIdx = series.map((p, i) => (p.solid ? i : -1)).filter((i) => i >= 0);
	const line = solidIdx.map((i, k) => `${k === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(series[i].share).toFixed(1)}`).join(' ');
	const area = solidIdx.length > 1
		? `${line} L${xAt(solidIdx[solidIdx.length - 1]).toFixed(1)},${yAt(0).toFixed(1)} L${xAt(solidIdx[0]).toFixed(1)},${yAt(0).toFixed(1)} Z`
		: null;

	return (
		<div className="mt-4 border-t border-border pt-3">
			<div className="flex flex-wrap items-baseline justify-between gap-x-3">
				<div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Against you, over time</div>
				{headline ? (
					<div className="text-sm">
						<span className={`font-medium ${STANCE_META.against.text}`}>{Math.round(headline.share * 100)}% against</span>
						<span className="text-muted-foreground"> on {formatUTCDate(headline.x)} · {headline.n} replies</span>
					</div>
				) : (
					<div className="text-xs text-muted-foreground">No day has {MIN_DAY_BASE}+ replies yet</div>
				)}
			</div>
			<svg viewBox={`0 0 ${W} ${H}`} className="mt-1 w-full" style={{ height: H }} role="img" aria-label={headline ? `Share of replies against the account by day; on ${formatUTCDate(headline.x)} it was ${Math.round(headline.share * 100)} percent of ${headline.n} replies. Days with fewer than ${MIN_DAY_BASE} replies are shown faint.` : 'Share of replies against the account by day; no day has enough replies to read yet.'}>
				{[0, 0.5, 1].map((v) => (
					<g key={v}>
						<line x1={padL} x2={W - padR} y1={yAt(v)} y2={yAt(v)} stroke="currentColor" className="text-border" strokeDasharray="3 3" strokeWidth="1" />
						<text x={padL - 6} y={yAt(v) + 3} textAnchor="end" className="fill-muted-foreground" style={{ fontSize: 9 }}>{Math.round(v * 100)}</text>
					</g>
				))}
				{area && <path d={area} className="fill-rose-600/10" />}
				{solidIdx.length > 1 && <path d={line} className="stroke-rose-600" strokeWidth="2" fill="none" strokeLinejoin="round" strokeLinecap="round" />}
				{series.map((p, i) => (
					<circle
						key={p.x}
						cx={xAt(i)} cy={yAt(p.share)}
						r={p.solid ? 3.5 : 2.5}
						className={p.solid ? 'fill-rose-600' : 'fill-rose-300/60'}
					>
						<title>{`${formatUTCDate(p.x)} · ${p.against} against of ${p.n}${p.solid ? '' : ' (too few to read)'}`}</title>
					</circle>
				))}
			</svg>
			<div className="mt-1 text-[11px] text-muted-foreground">Faint points are days with fewer than {MIN_DAY_BASE} replies; the line joins the rest.</div>
		</div>
	);
}

export function PulseTimeline({ points = [], loading }) {
	const [hover, setHover] = useState(null);
	const keys = useMemo(() => CHART_ORDER.map((e) => ({ key: e, color: EMOTION_META[e].hex })), []);
	const series = useMemo(() => points.map((p) => ({ x: p.bucketStart, values: p.emotions || {}, stance: p.stance })), [points]);
	const total = points.reduce((a, p) => a + (p.replyCount || 0), 0);

	return (
		<Card>
			<CardContent className="p-5">
				<div className="flex items-baseline justify-between gap-3">
					<div>
						<div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">The pulse</div>
						<div className="text-sm text-muted-foreground">{total} replies over {points.length} {points.length === 1 ? 'day' : 'days'}</div>
					</div>
					{hover && (
						<div className="text-right text-sm">
							<div className="font-medium text-foreground">{formatUTCDate(hover.x)}</div>
							<div className="text-muted-foreground">
								{CHART_ORDER.filter((e) => hover.values[e] > 0).slice(0, 3).map((e) => `${EMOTION_META[e].label} ${hover.values[e]}`).join(' · ')}
								{hover.stance && (hover.stance.against || 0) > 0 && <span className={STANCE_META.against.text}> · {hover.stance.against} against you</span>}
							</div>
						</div>
					)}
				</div>
				<div className="mt-3">
					<SeriesChart points={series} keys={keys} height={200} ariaLabel="Audience emotions over time" emptyText={loading ? 'Loading the pulse…' : 'No pulse yet - take the first one.'} onHover={(p) => setHover(p)} />
				</div>
				<div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
					{CHART_ORDER.map((e) => (
						<span key={e} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><span className={`inline-block h-2.5 w-2.5 rounded-sm ${EMOTION_META[e].bg}`} />{EMOTION_META[e].label}</span>
					))}
				</div>
				<AgainstLine points={points} />
			</CardContent>
		</Card>
	);
}
